import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Buildings, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TrustsAdmin() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    api
      .get("/admin/trusts")
      .then((r) => setRows(r.data || []))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load trusts");
      });
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/trusts", { name: name.trim(), slug: slug.trim() || undefined });
      toast.success("Trust created — workspace starts blank. Add hostels & schools next.");
      setName(""); setSlug("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout title="Trusts" subtitle="Strict multi-tenant isolation — each trust is a sealed workspace.">
      <div
        className="vs-card p-4 mb-4 flex items-start gap-3 border-l-4 border-[var(--vs-secondary)]"
        data-testid="trust-security-banner"
      >
        <ShieldCheck size={20} className="text-[var(--vs-secondary)] flex-shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold mb-1">Multi-Trust Data Security Policy</div>
          <div className="text-[var(--vs-text-secondary)]">
            Super admins can view and create across every trust by explicitly choosing the trust during create.
            There is no &quot;active workspace&quot; switch — preventing accidental cross-trust data leakage. New trusts
            start completely blank; you create their hostels, schools and users one by one.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={create} className="vs-card p-5 space-y-3 lg:col-span-1" data-testid="trust-create-form">
          <div className="eyebrow flex items-center gap-1.5"><Plus size={12} /> Add new trust</div>
          <div>
            <Label>Name *</Label>
            <Input data-testid="trust-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Other Charitable Trust" />
          </div>
          <div>
            <Label>Slug <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(optional)</span></Label>
            <Input data-testid="trust-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-derived from name" />
          </div>
          <Button type="submit" disabled={busy} data-testid="trust-create-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            {busy ? "Creating…" : "Create trust"}
          </Button>
        </form>

        <div className="lg:col-span-2 vs-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--vs-bg-subtle)]">
              <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                <th className="px-4 py-3">Trust</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Trust ID</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-[var(--vs-border)]" data-testid={`trust-row-${t.slug}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><Buildings size={14} /> {t.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[var(--vs-text-secondary)]">{t.id}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={t.status === "active" ? "border-[var(--vs-success)]/40 text-[var(--vs-success)]" : "border-[var(--vs-text-secondary)]/40"}>{t.status}</Badge>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No trusts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
